




















import { Component, Vue, Watch } from "vue-property-decorator";
import { State, Getter, Mutation } from "vuex-class";
import { ATMAsset, ATMPair } from "@foxone/api/src/models/market";
import { TradeSide } from "@foxone/api/src/models/market";
import Introduction from "./Introduction.vue";
import AssetsSelector from "./AssetsSelector.vue";
import axios, { CancelTokenSource } from "axios";

@Component({
  components: {
    Introduction,
    AssetsSelector
  }
})
class InstantTrade extends Vue {
  @State(state => state.trade.pairs) pairs!: ATMPair[];

  @Mutation("trade/SET_INSTANT_ASSETS") setInstantAssets;

  @Getter("trade/getAssets") assets!: ATMAsset[];

  offerAmount = "";

  wantedAmount = "";

  wantedAsset: any = "";

  offerAsset: any = "";

  priceImpact = 0;

  avgPrice = 0;

  lock = false;

  preordering = false;

  requestPreOrderFn: Function | null = null;

  cancelToken: CancelTokenSource | null = null;

  get side() {
    if (!this.pair || !this.wantedAsset) return "";
    return this.wantedAsset.asset_id === this.pair.base.asset_id
      ? TradeSide.BID
      : TradeSide.ASK;
  }

  get pair() {
    const { offerAsset, wantedAsset } = this;
    if (!offerAsset || !wantedAsset) return null;

    const offerAssetId = offerAsset.asset_id;
    const wantedAssetId = wantedAsset.asset_id;
    return this.pairs.find(
      ({ base, quote }) =>
        (base.asset_id === offerAssetId && quote.asset_id === wantedAssetId) ||
        (base.asset_id === wantedAssetId && quote.asset_id === offerAssetId)
    );
  }

  get pricePrecision() {
    const precision =
      this.side === TradeSide.BID
        ? this.wantedAsset?.precision ?? 0
        : this.offerAsset?.precision ?? 0;
    return Math.min(precision + 4, 8);
  }

  get estimatedRate() {
    if (!this.pair) return undefined;

    const toPrecision = this.$common.utils.number.toPrecision;
    const result =
      this.side === TradeSide.BID ? "" + 1 / +this.pair.price : this.pair.price;

    return {
      price: toPrecision(result, this.pricePrecision),
      reversePrice: toPrecision(1 / +result, this.pricePrecision)
    };
  }

  get avgPriceText() {
    const toPrecision = this.$common.utils.number.toPrecision;
    return toPrecision(this.avgPrice, this.pricePrecision);
  }

  @Watch("offerAmount")
  @Watch("wantedAsset")
  @Watch("offerAsset")
  handlePreOrder() {
    const finish = (amount = "") => {
      this.wantedAmount = amount;
      this.preordering = false;

      let priceImpact = 0;
      let avgPrice = 0;
      const currentPrice = +(this.estimatedRate?.price ?? "");
      if (this.offerAmount && currentPrice) {
        avgPrice = +amount / +this.offerAmount;
        priceImpact = (avgPrice - currentPrice) / currentPrice;
      }

      this.priceImpact = priceImpact;
      this.avgPrice = avgPrice;
    };

    const preOrder = async () => {
      if (!this.pair) return;

      const selector = this.$refs.assetSelector as any;
      if (!selector.meta.enabled || !selector.meta.valid) {
        finish();
        return;
      }

      this.preordering = true;

      if (this.cancelToken) {
        this.cancelToken.cancel("cancelled");
      }

      if (!this.offerAmount || +this.offerAmount <= 0) {
        finish();
        return;
      }

      this.cancelToken = axios.CancelToken.source();
      try {
        const order = await this.$api.market.preOrder(
          {
            funds: this.offerAmount,
            side: this.side,
            symbol: this.pair?.symbol
          },
          this.cancelToken.token
        );

        if (order.state !== "Filled") {
          this.$common.utils.helper.toast(this, {
            message: "Cannot filled this order",
            color: "error"
          });
          finish();
          return;
        }

        finish(order.filled_amount);
      } catch (error) {
        if (error.message !== "cancelled") {
          this.$common.utils.helper.errorHandler(this, error);
          finish();
        }
      }
    };

    if (!this.requestPreOrderFn) {
      this.requestPreOrderFn = this.$common.utils.helper.debounce(preOrder, 50);
    }
    this.requestPreOrderFn();
  }

  @Watch("wantedAsset")
  @Watch("offerAsset")
  handleAssetChange() {
    this.$store.commit("trade/SET_INSTANT_ASSETS", {
      wantedAsset: this.wantedAsset?.asset_id ?? "",
      offerAsset: this.offerAsset?.asset_id ?? ""
    });
  }

  async mounted() {
    let initWanted = this.$store.state.trade.instant.wantedAsset;
    let initOffer = this.$store.state.trade.instant.offerAsset;

    if (this.$route.query.wanted) {
      initWanted = this.$route.query.wanted;
    }

    if (this.$route.query.offer) {
      initOffer = this.$route.query.offer;
    }

    const wantedAsset = this.assets.find(asset => {
      return (
        asset.asset_id === initWanted ||
        asset.alia === initWanted ||
        asset.symbol === initWanted
      );
    });

    const offerAsset = this.assets.find(asset => {
      return (
        asset.asset_id === initOffer ||
        asset.alia === initOffer ||
        asset.symbol === initOffer
      );
    });

    if (wantedAsset) {
      this.wantedAsset = wantedAsset;
    }

    if (offerAsset) {
      this.offerAsset = offerAsset;
    }
  }
}
export default InstantTrade;
